import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { Subject } from "rxjs";
import { Roles, SystemConfigPromiseService } from "shared/auth-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrderAuditAdapter } from "shared/cubiq-services/mediator/adapter/OrderAuditAdapter";
import { BmarkFixingData } from "shared/cubiq-services/model/benchmarks/BmarkFixingData.model";
import { CoreBenchmarkAudit } from "shared/cubiq-services/model/core/CoreBenchmarkAudit";
import { CubiqWorker } from "shared/cubiq-services/worker/CubiqWorker";

const logger = LoggerService.for(__dirname);

/** CHeck every 2 minutes */
const FIXING_PURGE_INTERVAL = 1000 * 60 * 2;
/** If fixing has not been displayed in 3 mins, then purge */
const FIXING_PURGE_TIME = 1000 * 60 * 3;

/**
 * Internal class holding the details of a fixing that can be served to any requesting view
 */
class MonitoredFixing {
  /** Internal fixing key */
  fixingKey: string;
  /** Benchmark fixing ID */
  fixingTimeCode: CubiqTS.BMTimeCode;

  /** Promise for any pending call */
  primingPromise: Promise<CoreBenchmarkAudit[]>;

  /** List of audits received between request and priming - should normally be empty */
  pendingAudits: CubiqTS.BenchmarkPositionAudit[] = [];
  /** Set of all audit IDs in the audit records */
  auditIds: Set<string> = new Set();
  /** Main data payload for audits */
  fixingAudits: CoreBenchmarkAudit[] = [];
  /** Time when fixing was last used */
  releaseTime: number = 0;
}

export class BenchmarkAuditUpdateEvent {
  fixingCode: CubiqTS.BMTimeCode;
  audits: CoreBenchmarkAudit[];
}

/**
 * Service that retrieves Fixing audit logs from the server on a single-fixing basis and
 * caches these audit results locally. Once cached, the audits are kept updated from server
 * BM Audit events
 * The local audit maintains a time based on last view retrieval - this is used internally to purge
 * Audits that have not been referenced in a while.
 */
@Injectable()
export class BenchmarkAuditLookupService {
  private auditUpdateSubject: Subject<BenchmarkAuditUpdateEvent> = new Subject();

  /** Stream of update events for the currently actively viewed fixing */
  public auditUpdate$ = this.auditUpdateSubject.asObservable();
  private monitoredFixings: Map<string, MonitoredFixing> = new Map();

  private auditEnabled: boolean;

  private lastReferencedFixingId: string = null;

  private purgeTimer: any;

  constructor(
    protected cubiqWorker: CubiqWorker,
    protected sysConfigService: SystemConfigPromiseService,
    protected orderAuditAdapter: OrderAuditAdapter,
  ) {
    this.init();
  }

  protected init(): void {
    Promise.all([
      this.cubiqWorker.isAuth(`${this.constructor.name}`),
      this.sysConfigService.isPrimed(),
    ]).then(() => {
      // Only monitor fixing audits if user can see them
      this.sysConfigService
        .hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_AUDIT)
        .then((auditEnabled: boolean) => {
          this.auditEnabled = auditEnabled;
          if (auditEnabled) {
            this.subscribeForEvents();
            this.startPurgeTask();
          } else {
            logger.out.info(
              `${this.constructor.name}: Audit disabled. Disabling listening for fixing audit events.`,
            );
          }
        });
    });
  }

  /**
   * Clean up cache purge timer
   */
  ngOnDestroy() {
    if (this.purgeTimer) {
      clearInterval(this.purgeTimer);
    }
  }
  /**
   * Monitor server API for incoming audit changes
   */
  protected subscribeForEvents(): void {
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BenchmarkPositionAuditImpl.name)
      .subscribe((event: CubiqTS.BenchmarkPositionAuditImpl) => {
        try {
          this.processBenchmarkAuditEvent(event);
        } catch (e) {
          let bmCode: string = event?.bmTimeCode?.code;
          let bmTime: CubiqTS.Moment = event?.bmTimeCode?.time;

          logger.out.error(
            `${this.constructor.name}: Failed to process fixing audit ${bmCode}, time ${bmTime}`,
          );
        }
      });
  }

  private startPurgeTask(): void {
    this.purgeTimer = setInterval(() => {
      this.purgeFixings();
    }, FIXING_PURGE_INTERVAL);
  }

  /**
   * Iterate over all fixings and remove those whose releaseTime is before an expire time
   */
  private purgeFixings() {
    const now = new Date();
    const lastValidTime = now.getTime() - FIXING_PURGE_TIME;

    const purgeList = [];
    this.monitoredFixings.forEach((monitoredFixing, fixingId) => {
      if (monitoredFixing.releaseTime > 0) {
        if (monitoredFixing.releaseTime < lastValidTime) {
          logger.out.info(`Removing old audit l9ogs for fixing '${fixingId}'`);
          purgeList.push(fixingId);
        }
      }
    });
    // Deleted any old fixings we've found
    purgeList.forEach((key) => this.monitoredFixings.delete(key));
  }

  /**
   * Process a single Fixing Audit event
   * @param event
   */
  protected processBenchmarkAuditEvent(event: CubiqTS.BenchmarkPositionAuditImpl): void {
    if (event != null && event.bmTimeCode != null) {
      const fixingId = this.getFixingKey(event.bmTimeCode);
      const monitoredFixing: MonitoredFixing = this.monitoredFixings.get(fixingId);

      if (monitoredFixing) {
        if (monitoredFixing.primingPromise) {
          // We have requested fixing data but it's not yet been primed
          monitoredFixing.pendingAudits.push(event);
        } else {
          const auditKey = event.auditId?.toString() || "BAD_ID";
          if (!monitoredFixing.auditIds[auditKey]) {
            monitoredFixing.auditIds.add(auditKey);
            const auditRecord = this.createAuditRecord(event);
            monitoredFixing.fixingAudits.unshift(auditRecord);
            this.notifyFixingChange(monitoredFixing);
          } else {
            logger.out.warn(`Ignoring Fixing audit - key ${auditKey} already exists`);
          }
        }
      } else {
        logger.out.info(`Ingoring bmark audit for unmonitored fixed: ${fixingId}`);
      }
    }
  }

  private createAuditRecord(audit: CubiqTS.BenchmarkPositionAudit): CoreBenchmarkAudit {
    return this.orderAuditAdapter.transformBenchmarkAudit(audit);
  }

  /**
   * Send out event on update stream if there's any changes to the single active fixing
   * @param monitoredFixing
   */
  notifyFixingChange(monitoredFixing: MonitoredFixing) {
    if (monitoredFixing.fixingKey == this.lastReferencedFixingId) {
      // Only push an event for a fixing that's being actively shown
      const event = new BenchmarkAuditUpdateEvent();
      event.fixingCode = monitoredFixing.fixingTimeCode;
      event.audits = monitoredFixing.fixingAudits;

      this.auditUpdateSubject.next(event);
    }
  }

  protected getFixingKey(fixing: CubiqTS.BMTimeCodeImpl): string {
    const utcMoment = fixing.time ? CubiqTS.moment(fixing.time).utc() : null;
    return BmarkFixingData.generateFixingIdFromCodeMoment(fixing?.code, utcMoment, null);
  }

  /**
   * Return a promise containing a list of CoreBEnchmarkAudits. Data is either already cached locally or retrieved
   * from the server
   * @param fixingCode
   * @returns
   */
  public getAuditDataForFixing(fixingCode: CubiqTS.BMTimeCode): Promise<CoreBenchmarkAudit[]> {
    if (!this.auditEnabled) {
      return Promise.reject("Benchmark audits are not marked as enabled");
    }

    const fixingId = this.getFixingKey(fixingCode);

    let activeFixing: MonitoredFixing = this.monitoredFixings.get(fixingId);
    if (activeFixing) {
      this.designateCurrentFixing(activeFixing);
      if (activeFixing.primingPromise) {
        return activeFixing.primingPromise;
      }
      return Promise.resolve(activeFixing.fixingAudits);
    } else {
      const retPromise = this.primeFixingFromServer(fixingCode, fixingId);
      return retPromise;
    }
  }

  /**
   * Ensure fixing is not a candidate for purging and enable any other fixing to be GCed
   * @param monitoredFixing
   */
  private designateCurrentFixing(monitoredFixing: MonitoredFixing) {
    if (this.lastReferencedFixingId) {
      const oldFixing = this.monitoredFixings.get(this.lastReferencedFixingId);
      if (oldFixing) {
        oldFixing.releaseTime = new Date().getTime();
      }
    }
    if (monitoredFixing) {
      monitoredFixing.releaseTime = 0;
      this.lastReferencedFixingId = monitoredFixing.fixingKey;
    }
  }

  /**
   * Create a request and send to the server. On response, update model then resolve the promise
   * @param fixingCode
   * @param fixingId
   * @returns
   */
  private primeFixingFromServer(
    fixingCode: CubiqTS.BMTimeCode,
    fixingId: string,
  ): Promise<CoreBenchmarkAudit[]> {
    logger.out.info(`Retrieving Fixing audit from server for fixing ID ${fixingId}`);
    // Need to go to the server to get the fixing audit records
    const retPromise: Promise<CoreBenchmarkAudit[]> = new Promise(async (res, rej) => {
      let recentFixingsRequest = this.createRequest(fixingCode);
      try {
        let response: CubiqTS.FixingStatusResponse = await this.cubiqWorker.postServiceRequest(
          recentFixingsRequest,
        );
        if (response?.benchmarkPositionAudits) {
          // Add the server audits and augment with any pending records
          this.initialiseMonitoredFixing(monitoredFixing, response.benchmarkPositionAudits);
          this.designateCurrentFixing(monitoredFixing);
          res(monitoredFixing.fixingAudits);
        } else {
          rej("Error retrieving fixing audit details");
        }
      } catch (e) {
        logger.out.warn("Error getting fixing", e);
        rej(e);
      }
    });

    const monitoredFixing = new MonitoredFixing();
    monitoredFixing.fixingKey = fixingId;
    monitoredFixing.fixingTimeCode = fixingCode;
    monitoredFixing.primingPromise = retPromise;

    this.monitoredFixings.set(fixingId, monitoredFixing);
    return retPromise;
  }

  /**
   * Initialise the NonitoredFixing with all the server-returned audit records and
   * append any additional audit records that are also pending for the fixing
   * @param monitoredFixing
   * @param benchmarkPositionAudits
   */
  private initialiseMonitoredFixing(
    monitoredFixing: MonitoredFixing,
    benchmarkPositionAudits: CubiqTS.BenchmarkPositionAudit[],
  ) {
    for (let audit of benchmarkPositionAudits) {
      const auditId = audit.auditId?.toString();
      monitoredFixing.auditIds.add(auditId);
    }
    monitoredFixing.fixingAudits = benchmarkPositionAudits.map((event) =>
      this.orderAuditAdapter.transformBenchmarkAudit(event),
    );
    for (let audit of monitoredFixing.pendingAudits) {
      const auditId = audit.auditId?.toString();
      if (!monitoredFixing.auditIds.has(auditId)) {
        monitoredFixing.fixingAudits.push(this.orderAuditAdapter.transformBenchmarkAudit(audit));
        monitoredFixing.auditIds.add(auditId);
      }
    }
    monitoredFixing.fixingAudits.sort((audit1, audit2) => {
      return audit2.actionTime - audit1.actionTime;
    });
    monitoredFixing.primingPromise = null;
  }

  private createRequest(bmTimeCode: CubiqTS.BMTimeCode): CubiqTS.FixingStatusRequestImpl {
    const retRequest = new CubiqTS.FixingStatusRequestImpl();
    retRequest.bMTimeCode = bmTimeCode;
    retRequest.includeAudits = true;

    return retRequest;
  }
}
